<template>
	<div v-if="ad && renderAd">
		<div
			v-show="adVisible"
			class="image-renderer"
			:class="{
				'is-mobile': isMobileWebApp,
				'is-portrait': isMobileWebAppPortrait,
				'is-landscape': isMobileWebAppLandscape,
				'monetization-logo': type === AdType.PLAYER_LOGO,
				'monetization-banner': type === AdType.PLAYER_BANNER,
				'cursor-pointer': adLink,
				'has-loaded': hasLoaded,
			}"
			:data-text="$I18n.trans('monetization.advertisement')"
			:style="style"
			@click.stop="clickHandler"
		>
			<AdButlerAd
				v-if="ad.isAdbutlerAd"
				:ad="ad"
				:fixed-size="!dynamicResizing"
				@loaded="adbutlerLoaded"
			/>
			<img
				v-else-if="adVisible"
				ref="image"
				class="image-renderer__image"
				:src="image"
			>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { EVENT, bus } from '@videotools/js/events';
import storage from '@shared/js/storage';
import AdButlerAd from '@components/Ads/AdButlerAd.vue';
import { AdType } from '@shared/enums/AdType';

// Image display mode
const TEN_SECONDS_EACH_FIVE_MINUTES = '10s_each_5m';
const TEN_SECONDS_ONCE = '10s_once';

const PLAYER_AD = 'player_ad';

export default {
	name:       'MonetizationImageRenderer',
	components: { AdButlerAd },
	props:      {
		playerIsAdMode: {
			type:    Boolean,
			default: false,
		},
		position: {
			type: Number,
		},
		type: {
			type:      String,
			required:  true,
			validator: value => [AdType.PLAYER_LOGO, AdType.PLAYER_BANNER].includes(value),
		},
		playerWidth: {
			type:    Number,
			default: null,
		},
		event: {
			type:    Object,
			default: null,
		},
		ads: {
			type:    Array,
			default: () => [],
		},
		isCouch: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			timeout:           null,
			originalHeight:    null,
			originalWidth:     null,
			renderAd:          false,
			lastImageLoadedTs: null,
			positionUpdated:   false,
			hasLoaded:         false,
			AdType,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:          'app/isMobileWebApp',
			isMobileWebAppPortrait:  'app/isMobileWebAppPortrait',
			isMobileWebAppLandscape: 'app/isMobileWebAppLandscape',
			isAnalysisMode:          'app/isAnalysisMode',
		}),
		style() {
			if (!this.playerWidth ||
				this.type !== AdType.PLAYER_LOGO ||
				!this.originalHeight ||
				!this.originalWidth
			) return {};

			// Default container 240x240 for 1920px width
			// todo controls2: scale in PlayerOverlays.vue
			const containerLimit = Math.round(this.playerWidth * 140 / 1920);
			const ratio = Math.min(containerLimit / this.originalWidth, containerLimit / this.originalHeight);

			return {
				width:  `${this.originalWidth * ratio}px`,
				height: `${this.originalHeight * ratio}px`,
			};
		},
		// Render ad based on position time
		adVisible() {
			if (
				this.playerIsAdMode ||
				this.isCouch ||
				this.isAnalysisMode ||
				!this.hasMonetizationAds ||
				!this.renderAd ||
				!this.positionUpdated
			) {
				return false;
			}

			if (this.displayMode === TEN_SECONDS_EACH_FIVE_MINUTES) {
				const FIVE_MINUTES = 5 * 60;
				const TEN_SECONDS = 10;
				const periodPosition = this.position % FIVE_MINUTES;
				return periodPosition < TEN_SECONDS;
			} else if (this.displayMode === TEN_SECONDS_ONCE) {
				return Boolean(this.timeout);
			}
			return true; // Permanent;
		},
		displayMode() {
			return this.ad?.displayMode;
		},
		hasMonetizationAds() {
			return this.ads && this.ads.length;
		},
		image() {
			return this.ad?.imageUrl;
		},
		adLink() {
			return this.ad?.link;
		},
		hasVast() {
			return this.ads.find(ad => ad.type === PLAYER_AD);
		},
		ad() {
			return this.ads?.find(ad => ad.type === this.type);
		},
		dynamicResizing() {
			return this.type === AdType.PLAYER_LOGO;
		},
	},
	watch: {
		adVisible: function(visible) {
			this.lastImageLoadedTs = Date.now();
			if (visible) {
				this.$nextTick(() => {
					if (this.$refs.image && !this.hasLoaded) {
						this.$refs.image.onload = () => {
							this.hasLoaded = true;
						};
					}
				});
			}
		},
		position() {
			this.positionUpdated = true;
		},
		ad() {
			if (this.ad?.isAdbutlerAd) {
				this.renderAd = true;
			}
		},
	},
	mounted() {
		if (this.image) {
			if (this.type === AdType.PLAYER_LOGO) {
				const img = new Image();
				const vm = this;
				img.onload = function() {
					vm.originalHeight = this.naturalHeight;
					vm.originalWidth = this.naturalWidth;
					vm.renderAd = true;
				};
				img.src = this.image;
			} else {
				this.renderAd = true;
			}

			this.$nextTick(() => {
				if (this.$refs.image && !this.hasLoaded) {
					this.$refs.image.onload = () => {
						this.hasLoaded = true;
					};
				}
			});
		}

		if (this.ad?.isAdbutlerAd) {
			this.renderAd = true;
		}

		if (this.displayMode === TEN_SECONDS_ONCE) {
			if (!this.hasVast) {
				this.handleTenSecondsAd();
			} else {
				bus.subscribe(EVENT.PLAYER.ON.MONETIZATION_AD_START, this.handleTenSecondsAd);
			}
		}
	},
	destroyed() {
		clearTimeout(this.timeout);
		if (this.hasVast && this.displayMode === TEN_SECONDS_ONCE) bus.unsubscribe(EVENT.PLAYER.ON.MONETIZATION_AD_START, this.handleTenSecondsAd);
	},
	methods: {
		clickHandler() {
			if (this.adLink) window.open(this.appendAdAttributes(this.adLink), '_blank');
		},
		handleTenSecondsAd() {
			const TEN_SECONDS_TIMEOUT = 10000;

			this.timeout = setTimeout(() => {
				this.timeout = null;
			}, TEN_SECONDS_TIMEOUT);
		},
		appendAdAttributes(url, appendRandomHash = false) {
			if (!url) {
				return url;
			}

			let imageUrl = this.event ? `${url}/${this.event.id}` : url;

			if (this.event && storage.viewerId) {
				imageUrl = `${imageUrl}/${storage.viewerId}`;
			}

			if (appendRandomHash) {
				imageUrl = `${imageUrl}/${this.getCurrentTimeHash()}`;
			}

			return imageUrl;
		},
		getCurrentTimeHash() {
			return moment(this.lastImageLoadedTs || Date.now()).startOf('minute').unix();
		},
		adbutlerLoaded(adPlacement) {
			if (adPlacement) {
				this.hasLoaded = true;

				if (adPlacement.width && adPlacement.height) {
					this.originalWidth = adPlacement.width;
					this.originalHeight = adPlacement.height;
				}
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
	cursor: pointer;
}

.monetization-banner {
	position: relative;
	color: var(--color-soft-white);
	margin: 0 auto;

	&:not(.is-mobile) {
		max-width: 728px;

		@media screen and (max-width: 1200px) {
			max-width: 80%;
		}
	}

	&.is-mobile {
		&.is-portrait {
			width: 100%;
			height: auto;
		}

		&.is-landscape {
			max-width: 80%;
			max-height: 60px;
		}
	}

	&.has-loaded::after {
		display: block;
		content: attr(data-text);
		position: absolute;
		right: 2px;
		bottom: calc(100% - -8px);
		font-size: 14px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
		color: var(--color-white);
	}

	&.has-loaded.is-mobile::after {
		right: 0;
		top: 0;
		padding: 8px 5px;
		background: white;
		color: var(--color-black);
		font-size: 12px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
	}
}

.image-renderer {
	&:empty {
		display: none;
	}
}

.image-renderer__image {
	.image-renderer.monetization-banner & {
		width: 100%;
	}
}
</style>
